/*
 * Bello — Capsule World Carousel
 *
 * One card per capsule a bundle carries. The track is a native scroll
 * container with CSS snap points, so the swipe itself is the browser's:
 * momentum on touch, trackpad on desktop, arrow keys and Tab for free.
 * This file only reflects the scroll position back into the chrome —
 * dots, tab row, "01 — 04", the two peek columns and the arrow buttons.
 *
 * Nothing here animates anything. Anything that moves is either scroll or
 * a CSS transition, which is what keeps the swap smooth on a mid-range
 * phone and what makes prefers-reduced-motion a one-line concern.
 *
 * The maths lives in pure functions at the top so it can be tested without
 * a DOM: see tests/bello-capsule-world-carousel.test.js.
 */
(function (global) {
  'use strict';

  /* ---------------------------------------------------------------- *
   * Pure helpers
   * ---------------------------------------------------------------- */

  function clampIndex(index, count) {
    var n = Math.round(Number(index));
    if (!isFinite(n) || n < 0) return 0;
    if (count <= 0) return 0;
    return Math.min(n, count - 1);
  }

  /* Which slide is active is derived from scroll position, never from
     IntersectionObserver: `isIntersecting` is true for any overlap at all, so
     on the first callback every partially visible slide reports in and the
     last one wins — which lights the wrong dot. Same reasoning as
     bello-taste-profile. */
  function nearestIndex(centres, viewportCentre) {
    var closest = 0;
    var shortest = Infinity;
    for (var i = 0; i < centres.length; i++) {
      var distance = Math.abs(centres[i] - viewportCentre);
      if (distance < shortest) {
        shortest = distance;
        closest = i;
      }
    }
    return closest;
  }

  /* "01 — 04": a leading zero up to nine, then the number as it is. */
  function pad2(value) {
    var n = Math.max(0, Math.round(Number(value) || 0));
    return n < 10 ? '0' + n : String(n);
  }

  /* The two peek columns name the capsule either side of the one on screen.
     At the ends there is nothing to name and the column goes quiet. */
  function neighbours(index, names) {
    var count = names ? names.length : 0;
    var i = clampIndex(index, count);
    return {
      prev: i > 0 ? names[i - 1] : '',
      next: i < count - 1 ? names[i + 1] : '',
      hasPrev: i > 0,
      hasNext: i < count - 1
    };
  }

  var api = {
    clampIndex: clampIndex,
    nearestIndex: nearestIndex,
    pad2: pad2,
    neighbours: neighbours
  };

  global.BelloCapsuleWorld = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  if (typeof document === 'undefined') return;

  /* ---------------------------------------------------------------- *
   * Wiring
   * ---------------------------------------------------------------- */

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function revealBars(root) {
    var groups = root.querySelectorAll('[data-bcw-bars]');
    if (!groups.length) return;

    if (reduced() || !('IntersectionObserver' in window)) {
      for (var i = 0; i < groups.length; i++) groups[i].classList.add('is-revealed');
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.25 }
    );

    for (var j = 0; j < groups.length; j++) observer.observe(groups[j]);
  }

  function init(root) {
    if (!root || root.__bcwReady) return;
    root.__bcwReady = true;

    var track = root.querySelector('[data-bcw-track]');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-bcw-slide]'));
    if (slides.length === 0) return;

    var names = slides.map(function (slide) {
      return slide.getAttribute('data-bcw-name') || '';
    });

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-bcw-dot]'));
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-bcw-tab]'));
    var prevButton = root.querySelector('[data-bcw-prev]');
    var nextButton = root.querySelector('[data-bcw-next]');
    var peekPrev = root.querySelector('[data-bcw-peek="prev"]');
    var peekNext = root.querySelector('[data-bcw-peek="next"]');
    var current = root.querySelector('[data-bcw-current]');

    var index = 0;

    revealBars(root);

    function paintPeek(peek, name, present, label) {
      if (!peek) return;
      var text = peek.querySelector('[data-bcw-peek-label]');
      if (text) text.textContent = name;
      if (present) {
        peek.removeAttribute('hidden');
        peek.removeAttribute('tabindex');
        peek.setAttribute('aria-label', label.replace('%s', name));
      } else {
        peek.setAttribute('hidden', 'hidden');
        peek.setAttribute('tabindex', '-1');
      }
    }

    function sync() {
      dots.forEach(function (dot, position) {
        dot.setAttribute('aria-current', position === index ? 'true' : 'false');
      });

      tabs.forEach(function (tab, position) {
        tab.setAttribute('aria-current', position === index ? 'true' : 'false');
      });

      slides.forEach(function (slide, position) {
        if (position === index) {
          slide.setAttribute('data-bcw-active', 'true');
        } else {
          slide.removeAttribute('data-bcw-active');
        }
      });

      if (current) current.textContent = pad2(index + 1);

      var edges = neighbours(index, names);
      if (prevButton) prevButton.disabled = !edges.hasPrev;
      if (nextButton) nextButton.disabled = !edges.hasNext;

      paintPeek(peekPrev, edges.prev, edges.hasPrev, root.getAttribute('data-bcw-prev-label') || 'Previous: %s');
      paintPeek(peekNext, edges.next, edges.hasNext, root.getAttribute('data-bcw-next-label') || 'Next: %s');
    }

    function centres() {
      return slides.map(function (slide) {
        return slide.offsetLeft - track.offsetLeft + slide.offsetWidth / 2;
      });
    }

    function goTo(next, focusTarget) {
      var target = clampIndex(next, slides.length);
      var slide = slides[target];
      if (!slide) return;

      index = target;
      track.scrollTo({
        left: slide.offsetLeft - track.offsetLeft + slide.offsetWidth / 2 - track.clientWidth / 2,
        behavior: reduced() ? 'auto' : 'smooth'
      });
      sync();

      if (focusTarget && focusTarget.focus) focusTarget.focus();
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goTo(Number(dot.getAttribute('data-bcw-dot')));
      });
    });

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        goTo(Number(tab.getAttribute('data-bcw-tab')));
      });
    });

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        goTo(index - 1);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        goTo(index + 1);
      });
    }

    if (peekPrev) {
      peekPrev.addEventListener('click', function () {
        goTo(index - 1);
      });
    }

    if (peekNext) {
      peekNext.addEventListener('click', function () {
        goTo(index + 1);
      });
    }

    /* Left/right on a dot or a tab moves the carousel and keeps the focus
       with the control the reader is already on. */
    root.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      var group = dots.indexOf(event.target) > -1 ? dots : tabs.indexOf(event.target) > -1 ? tabs : null;
      if (!group) return;
      event.preventDefault();
      var next = clampIndex(index + (event.key === 'ArrowRight' ? 1 : -1), slides.length);
      goTo(next, group[next]);
    });

    var ticking = false;
    function readScroll() {
      ticking = false;
      var closest = nearestIndex(centres(), track.scrollLeft + track.clientWidth / 2);
      if (closest === index) return;
      index = closest;
      sync();
    }

    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(readScroll);
      },
      { passive: true }
    );

    // Read once the slides have their final width. Measuring synchronously
    // reads zero-width slides, picks the wrong index, and nothing corrects it
    // afterwards because no scroll event ever fires.
    requestAnimationFrame(readScroll);
    window.addEventListener('resize', readScroll, { passive: true });

    sync();
  }

  function scan(root) {
    var sections = (root || document).querySelectorAll('[data-bcw]');
    for (var i = 0; i < sections.length; i++) init(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})(typeof globalThis !== 'undefined' ? globalThis : this);
